/**
 * analytics.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Lightweight funnel-event recorder.
 *
 * We don't have Firebase Analytics wired yet (no `measurementId` on the
 * firebaseConfig), and dropping in a 3rd-party tool (Mixpanel/Amplitude)
 * brings a tracking beacon + cookie disclosure conversation we don't want
 * mid-beta. Instead, named funnel events are mirrored into a Firestore
 * collection `analyticsEvents` that the admin panel can aggregate.
 *
 * Scope is intentionally small — don't fire an event on every button click.
 * Use these for the signup → onboarded → first-wine → retention funnel and a
 * handful of commerce events (upgrade clicked, checkout completed).
 *
 * Event key conventions:
 *   signup                  First auth event, profile row created.
 *   onboarding_complete     User finished the onboarding quiz.
 *   first_wine_added        First wine landed in their cellar.
 *   upgrade_clicked         Any tier-upgrade button was pressed.
 *   checkout_completed      Stripe checkout returned success.
 *   report_submitted        User submitted a content report.
 *
 * Notes:
 * - Writes are fire-and-forget. A dropped event cannot be allowed to break a
 *   user flow.
 * - `params` is merged shallow; keep it to small primitives (strings,
 *   numbers, booleans). Don't pass whole wine objects.
 * - If Firebase Analytics is enabled later, `trackEvent` becomes a dual
 *   dispatch. The data model here is intentionally compatible with
 *   `logEvent(analytics, name, params)`.
 * ─────────────────────────────────────────────────────────────────────────────
 */

import { addDoc, collection, serverTimestamp } from 'firebase/firestore'
import { auth, db } from '../firebase.js'

// Per-session dedupe — prevents a user from accidentally double-firing
// `first_wine_added` because of a retry click, etc.
const SESSION_SEEN_ONCE = new Set()

export function trackEvent(name, params = {}, { onceInSession = false } = {}) {
  if (!name) return
  if (onceInSession) {
    if (SESSION_SEEN_ONCE.has(name)) return
    SESSION_SEEN_ONCE.add(name)
  }
  // Normalize: strip nested objects from params so we don't bloat Firestore
  // or accidentally log PII we didn't intend to.
  const cleanParams = {}
  for (const [k, v] of Object.entries(params)) {
    if (v === null || v === undefined) continue
    const t = typeof v
    if (t === 'string' || t === 'number' || t === 'boolean') cleanParams[k] = v
  }
  addDoc(collection(db, 'analyticsEvents'), {
    name,
    params: cleanParams,
    uid:   auth.currentUser?.uid || null,
    email: auth.currentUser?.email || null,
    path:  typeof window !== 'undefined' ? window.location.pathname.slice(0, 200) : '',
    createdAt: serverTimestamp(),
  }).catch(err => {
    // Swallow — analytics must never break a user flow.
    if (typeof console !== 'undefined') console.warn('[analytics] write failed', err)
  })
}

